import { API_ENDPOINTS } from '@/utils/apiConfig';

import b3Fetch from './fetch';

// Field mapping between frontend names and OData properties
const ORDERS_SORT_FIELD_MAPPING: Record<string, string> = {
  bcOrderId: 'ID',
  createdAt: 'CreationDateTime',
  status: 'LifeCycleStatusCode',
  cancellationStatus: 'CancellationStatusCode',
  companyName: 'SalesUnitParty/SalesUnitPartyName/FormattedName',
  placedBy: 'BuyerParty/BuyerPartyName/FormattedName',
  totalIncTax: 'NetAmount',
};

// Utility function to parse SAP's date format
function parseSAPDate(dateStr: string): Date | null {
  const match = dateStr?.match(/\/Date\((\d+)\)\//);
  return match ? new Date(parseInt(match[1], 10)) : null;
}

// Formats date for OData query, setting time to start or end of day
function formatDateParam(dateInput: string | number, isEndOfDay = false): string {
  if (!dateInput) return '';

  let date;

  if (typeof dateInput === 'number' || !Number.isNaN(Number(dateInput))) {
    date = new Date(Number(dateInput) * 1000);
  } else if (typeof dateInput === 'string') {
    date = new Date(`${dateInput}T00:00:00Z`);
  } else {
    throw new Error('Invalid date format!');
  }

  if (isEndOfDay) {
    date.setUTCHours(23, 59, 59, 999);
  } else {
    date.setUTCHours(0, 0, 0, 0);
  }

  // Returns ISO string without milliseconds (e.g. "2025-02-01T00:00:00Z")
  return `${date.toISOString().split('.')[0]}Z`;
}

// Helper to build the order by clause
function buildOrderBy(params: any, fieldMapping: Record<string, string>): string {
  let orderBy = 'CreationDateTime desc'; // Default sort
  if (params.orderBy) {
    const sortByOrder = params.orderBy.startsWith('-') ? 'desc' : 'asc';
    const sortByField = params.orderBy.replace('-', '').trim();
    const odataField = fieldMapping[sortByField];
    if (odataField) {
      orderBy = `${odataField} ${sortByOrder}`;
    } else {
      console.warn(`No mapping found for sort field: ${sortByField}. Using default ordering.`);
    }
  }
  return orderBy;
}

export const getSalesOrders = async (accountId: string, params: any) => {
  try {
    // Validate essential parameters
    if (!accountId) throw new Error('Account ID is required.');
    if (params == null || typeof params !== 'object')
      throw new Error('Invalid parameters provided.');

    console.log('Fetching Sales Orders with params:', params);

    let filter = `BuyerParty/PartyID eq '${accountId}' and SalesOrganisationID eq '1000'`; // Default filter
    // Add filters
    if (params.orderId) {
      filter += ` and ID eq '${params.orderId}'`;
    }

    if (params.statusCode) {
      filter += ` and LifeCycleStatusCode eq '${params.statusCode}'`;
    } else {
      // defaults to exclude "In Preparation" status
      // These are mutually exclusive? (we can't have both at the same time)
      filter += ` and LifeCycleStatusCode ne '4'`; // Exclude "In Preparation" status
    }

    if (params.orderCancellationStatusCode) {
      filter += ` and CancellationStatusCode eq '${params.orderCancellationStatusCode}'`;
    }

    const dateFitlers = [];
    if (params.beginDateAt) {
      dateFitlers.push(
        `CreationDateTime ge datetimeoffset'${formatDateParam(params.beginDateAt)}'`,
      );
    }
    if (params.endDateAt) {
      dateFitlers.push(`CreationDateTime le datetimeoffset'${formatDateParam(params.endDateAt)}'`);
    }

    if (dateFitlers.length) {
      filter += `and (${dateFitlers.join(' and ')})`;
    }

    // Handle sorting
    const orderBy = buildOrderBy(params, ORDERS_SORT_FIELD_MAPPING);

    // Build URL
    const baseUrl = API_ENDPOINTS.getSalesOrders;

    const queryParams = new URLSearchParams({
      $format: 'json',
      $top: params.first.toString(),
      $skip: params.offset.toString(),
      $orderby: orderBy,
      $expand: 'BuyerParty/BuyerPartyName,DocumentReference,SalesUnitParty/SalesUnitPartyName,PriceAndTaxCalculation',
      $filter: filter,
      $inlinecount: 'allpages',
    });

    const url = `${baseUrl}?${queryParams.toString()}`;
    console.log('url:', url);
    const response = await b3Fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: { Accept: 'application/json' },
      credentials: 'include',
    });

    if (!response || !response.results) {
      throw new Error('Unexpected response format');
    }

    const { __count: count, results = [] } = response;

    console.log('Sales Orders Response:', results);

    // Map the response to the desired format
    const mappedData = {
      count: parseInt(count, 10),
      orders: results.map((order: any) => {
        const invoiceRefs = order.DocumentReference?.filter((ref: any) => ref.TypeCode === '28');
        const deliveryRefs = order.DocumentReference?.filter((ref: any) => ref.TypeCode === '73');
        return {
          id: order.ID,
          company: order.SalesUnitParty?.SalesUnitPartyName?.[0]?.FormattedName || 'N/A',
          orderNumber: order.ID,
          externalReference: order.ExternalReference || null,
          orderTotal: {
            amount: parseFloat(order.NetAmount),
            currency: order.NetAmountCurrencyCode,
          },
          documentReferences: {
            invoiceIds: invoiceRefs?.map((ref: any) => ref.ID) || [],
            deliveryIds: deliveryRefs?.map((ref: any) => ref.ID) || []
          },
          status: order.LifeCycleStatusCodeText,
          statusCode: order.LifeCycleStatusCode,
          cancellationStatus: order.CancellationStatusCodeText,
          cancellationStatusCode: order.CancellationStatusCode,
          buyer: order.BuyerParty?.BuyerPartyName?.[0]?.FormattedName || 'N/A',
          createdAt: parseSAPDate(order.CreationDateTime),
        };
      }),
    };
    

    console.log('Mapped Data:', mappedData);

    return mappedData;
  } catch (error) {
    console.error('Error fetching orders:', error);
    throw error;
  }
};

export const fetchSalesOrders = async (
  account_id: string,
  last_returned?: string,
  params?: any,
) => {
  try {
    let postingDateCode: number | null = null;

    let beginDateAt: any;
    let endDateAt: any = null;

    // If there are start or end dates, format them to ISO
    if (params?.beginDateAt || params?.endDateAt) {
      if (params?.beginDateAt) {
        const beginDate = new Date(params.beginDateAt);
        beginDate.setHours(0, 0, 0, 0);
        beginDateAt = beginDate.toISOString();
      }

      if (params?.endDateAt) {
        const endDate = new Date(params.endDateAt);
        endDate.setHours(23, 59, 59, 999);
        endDateAt = endDate.toISOString();
      }

      postingDateCode = beginDateAt ? (endDateAt ? 3 : 8) : 6;
    }

    const url = API_ENDPOINTS.getSalesOrderDetails;
    const body = JSON.stringify({
      account_id,
      last_returned,
      order_id: params?.orderId,
      begin_date: beginDateAt,
      end_date: endDateAt,
      posting_date_code: postingDateCode,
      status_code: params?.statusCode,
    });

    const init: RequestInit = {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
      body,
    };

    const response = await b3Fetch(url, init);
    return response;
  } catch (error) {
    console.error('Error', error);
    throw error;
  }
};

export const fetchInvoices = async (
  accountId: number,
  params: Record<string, any>,
  skip: number = 0,
  top: number = 10,
) => {
  try {
    const urlBase = API_ENDPOINTS.getInvoices;
    const expandFields =
      '$expand=Item,Item/ItemBusinessTransactionDocumentReference,BuyerParty,PaymentControl,CashDiscountTerms,PriceAndTaxCalculation';
    const orderByMap: Record<string, string> = {
      originalBalanceAmount: 'TotalGrossAmount asc',
      '-originalBalanceAmount': 'TotalGrossAmount desc',
      status: 'ReleaseStatusCode asc',
      '-status': 'ReleaseStatusCode desc',
      invoiceNumber: 'ID asc',
      '-invoiceNumber': 'ID desc',
      orderNumber: 'ID asc', // Todo add correct field
      '-orderNumber': 'ID desc',
      createdAt: 'CreationDateTime asc',
      '-createdAt': 'CreationDateTime desc',
    };
    const sortBy =
      params?.orderBy && orderByMap[params.orderBy]
        ? `$orderby=${orderByMap[params.orderBy]}`
        : '$orderby=CreationDateTime desc';
    const filters: string[] = [
      `BuyerParty/PartyID eq ${accountId}`, 
      `ReleaseStatusCode eq '3'`
    ];
    if (params?.invoiceID) {
      filters.push(`substringof('${params.invoiceID}', ID)`);
    }
    const invoiceDateFilters = [];
    if (params?.invoiceDateFrom) {
      invoiceDateFilters.push(
        `CashDiscountTerms/PaymentBaselineDate ge datetimeoffset'${formatDateParam(
          params.invoiceDateFrom,
        )}'`,
      );
    }
    if (params?.invoiceDateTo) {
      invoiceDateFilters.push(
        `CashDiscountTerms/PaymentBaselineDate le datetimeoffset'${formatDateParam(
          params.invoiceDateTo,
        )}'`,
      );
    }
    if (invoiceDateFilters.length) {
      filters.push(`(${invoiceDateFilters.join(' and ')})`);
    }
    const dueDateFilters = [];
    if (params?.beginDateAt) {
      dueDateFilters.push(
        `CashDiscountTerms/FullPaymentEndDate ge datetimeoffset'${formatDateParam(
          params.beginDateAt,
        )}'`,
      );
    }
    if (params?.endDateAt) {
      dueDateFilters.push(
        `CashDiscountTerms/FullPaymentEndDate le datetimeoffset'${formatDateParam(
          params.endDateAt,
        )}'`,
      );
    }
    if (dueDateFilters.length) {
      filters.push(`(${dueDateFilters.join(' and ')})`);
    }
    const queryParams = new URLSearchParams({
      $filter: filters.join(' and '),
      $format: 'json',
      $skip: skip.toString(),
      $top: top.toString(),
      $inlinecount: 'allpages',
    });
    const url = `${urlBase}?${expandFields}&${queryParams.toString()}&${sortBy}`;
    const init: RequestInit = {
      method: 'GET',
      mode: 'cors',
      headers: { Accept: 'application/json' },
      credentials: 'include',
    };
    const response = await b3Fetch(url, init);
    
    // Only keep invoices with PartyID === '1000' and still preserve the original pagination
    const filteredResults = response.results.filter(
      (invoice: any) => invoice.PartyID === '1000'
    );

    // TODO: Update the count to reflect the filtered results (if necessary, not now, Edilson Samuel 21.07.2025)
    return { ...response, results: filteredResults };
  } catch (error) {
    console.error('Error fetching invoices:', error);
    throw error;
  }
};


export const getInvoiceById = async (
  invoiceId: string,
) => {
  try {
    if (!invoiceId) {
      throw new Error('Invoice ID is required'); 
    }
    const urlBase = API_ENDPOINTS.getInvoices;
    const expandFields =
      '$expand=BuyerParty,PaymentControl,CashDiscountTerms,PriceAndTaxCalculation,Item';
    const filters: string[] = [`ID eq '${invoiceId}'`];


    const queryParams = new URLSearchParams({
      $filter: filters.join(' and '),
      $format: 'json',
      $inlinecount: 'allpages',
    });
    const url = `${urlBase}?${expandFields}&${queryParams.toString()}`;
    const init: RequestInit = {
      method: 'GET',
      mode: 'cors',
      headers: { Accept: 'application/json' },
      credentials: 'include',
    };
    const response = await b3Fetch(url, init);
    return response;
  } catch (error) {
    console.error('Error fetching invoice:', error);
    throw error;
  }
};

export const fetchDeliveries = async (
  accountId: number,
  params: Record<string, any>,
  skip: number = 0,
  top: number = 10,
) => {
  try {
    const orderByMap: Record<string, string> = {
      creationDate: 'CreationDateTime asc',
      '-creationDate': 'CreationDateTime desc',
      deliveryType: 'DeliveryTypeCode asc',
      '-deliveryType': 'DeliveryTypeCode desc',
      processingStatus: 'DeliveryProcessingStatusCode asc',
      '-processingStatus': 'DeliveryProcessingStatusCode desc',
      releaseStatus: 'ReleaseStatusCode asc',
      '-releaseStatus': 'ReleaseStatusCode desc',
      ID: 'ID asc',
      '-ID': 'ID desc',
    };

    const sortBy =
      params?.orderBy && orderByMap[params.orderBy]
        ? `$orderby=${orderByMap[params.orderBy]}`
        : '$orderby=CreationDateTime desc';

    const urlBase = API_ENDPOINTS.getDelivery;
    const expandFields = '$expand=Item,Item/ItemSalesOrderReference,Item/ItemDeliveryQuantity,Item/OutboundDelivery,Item/OutboundDelivery/ArrivalPeriod,Item/OutboundDelivery/ShippingPeriod,BuyerParty,SellerParty,FreightForwarderParty,ArrivalPeriod,ShippingPeriod,PickupPeriod';

    const filters: string[] = [`BuyerParty/PartyID eq ${accountId}`];

    if (params?.orderId) {
      if (params?.orderDatails) {
        filters.push(`ID eq '${params.orderId}'`);
      } else {
        filters.push(`substringof('${params.orderId}', ID)`);
      }
    }

    if (params.deliveryId) {
      if (params?.deliveryDatails) {
        filters.push(`ID eq '${params.deliveryId}'`);
      } else {
        filters.push(`substringof('${params.deliveryId}', ID)`);
      }
    }

    const creationDateFilters = [];
    if (params?.beginDateAt) {
      creationDateFilters.push(
        `CreationDateTime ge datetimeoffset'${formatDateParam(params.beginDateAt)}'`,
      );
    }
    if (params?.endDateAt) {
      creationDateFilters.push(
        `CreationDateTime le datetimeoffset'${formatDateParam(params.endDateAt)}'`,
      );
    }
    if (creationDateFilters.length) {
      filters.push(`(${creationDateFilters.join(' and ')})`);
    }

    if (params?.deliveryTypeStatus) {
      filters.push(`DeliveryTypeCode eq '${params.deliveryTypeStatus}'`);
    }

    if (params?.processingStatus) {
      filters.push(`DeliveryProcessingStatusCode eq '${params.processingStatus}'`);
    }

    if (params?.releaseStatus) {
      filters.push(`ReleaseStatusCode eq '${params.releaseStatus}'`);
    }

    const queryParams = new URLSearchParams({
      $filter: filters.join(' and '),
      $skip: skip.toString(),
      $top: top.toString(),
      $format: 'json',
      $inlinecount: 'allpages',
    });

    const url = `${urlBase}?${expandFields}&${queryParams.toString()}&${sortBy}`;

    const init: RequestInit = {
      method: 'GET',
      mode: 'cors',
      credentials: 'include',
    };

    const response = await b3Fetch(url, init);

    return response;
  } catch (error) {
    console.error('Error fetching deliveries:', error);
    throw error;
  }
};


export const getInvoice = async (
  invoice_id: string,
) => {
  try {

    if (!invoice_id) {
      throw new Error('Invoice ID is required');
    }

    const body = JSON.stringify({
      invoiceId: invoice_id,
    });

    const init: RequestInit = {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
      body,
    };

    const url = API_ENDPOINTS.getInvoice;
    const response = await b3Fetch(url, init);
    return response;
  } catch (error) {
    console.error('Error', error);
    throw error;
  }
};


export const getDocumentOutputRequest = async (
  invoice_uuid: string,
) => {
  try {

    if (!invoice_uuid) {
      throw new Error('Invoice ID is required');
    }

    const body = JSON.stringify({
      invoiceUuid: invoice_uuid,
    });

    const init: RequestInit = {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
      body,
    };

    const url = API_ENDPOINTS.getDocumentUUIDByInvoiceUUID;
    const response = await b3Fetch(url, init);
    return response;
  } catch (error) {
    console.error('Error', error);
    throw error;
  }
};

export const getInvoicePdf = async (
  invoice_id: string,
) => {
  try {

    if (!invoice_id) {
      throw new Error('Invoice ID is required');
    }

    const invoice = await getInvoice(invoice_id);

    if (!invoice?.CustomerInvoice?.UUID) {
      throw new Error('Invoice UUID not found');
    }
 
    const invoice_uuid = invoice.CustomerInvoice?.UUID;

    const documentOutPut = await getDocumentOutputRequest(invoice_uuid);

    if (!documentOutPut?.DocumentUUID) {
      throw new Error('Document UUID not found');
    }

    const body = JSON.stringify({
      documentUUID: documentOutPut.DocumentUUID,
    });

    const init: RequestInit = {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
      body,
    };
    
    const url = API_ENDPOINTS.getInvoicePdf;
    const response = await b3Fetch(url, init);

    if (!response?.DocumentOutputRequestPDF?.OutputPDF) {
      throw new Error('PDF not found');
    }

    return response?.DocumentOutputRequestPDF?.OutputPDF;
  } catch (error) {
    console.error('Error', error);
    throw error;
  }
};


export const getCustomerAccounts = async (
  accountId: number,
) => {
  try {
    const urlBase = API_ENDPOINTS.getCustomerAccounts;
    const filters: string[] = [`InternalID eq '${accountId}'`];

    const queryParams = new URLSearchParams({
      $filter: filters.join(' and '),
      $format: 'json',
    });

    const url = `${urlBase}?${queryParams.toString()}`;

    const init: RequestInit = {
      method: 'GET',
      mode: 'cors',
      credentials: 'include',
    };

    const response = await b3Fetch(url, init);

    return response;
  } catch (error) {
    console.error('Error fetching Customer Accounts:', error);
    throw error;
  }
};

export const fetchCustomerAccount = async (
  accountId: string,
) => {
  try {

    const url = API_ENDPOINTS.getCustomerAccount;
    const body = JSON.stringify({
      accountId,
    });

    const init: RequestInit = {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
      body,
    };

    const response = await b3Fetch(url, init);
    return response;
  } catch (error) {
    console.error('Error', error);
    throw error;
  }
};


export const fetchCompanyOpenAmounts = async (
  accountId: string,
) => {
  try {

    const url = API_ENDPOINTS.getCompanyOpenAmounts;
    const body = JSON.stringify({
      accountId,
    });

    const init: RequestInit = {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
      body,
    };

    const response = await b3Fetch(url, init);
    return response;
  } catch (error) {
    console.error('Error', error);
    throw error;
  }
};

export const getProductMaterial = async (
  product_id: string,
) => {
  try {

    if (!product_id) {
      throw new Error('Invoice ID is required');
    }

    const body = JSON.stringify({
      prod_internal_id: product_id,
    });

    const init: RequestInit = {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
      body,
    };

    const url = API_ENDPOINTS.getProductMaterial;
    const response = await b3Fetch(url, init);
    return response;
  } catch (error) {
    console.error('Error', error);
    throw error;
  }
};
